/**
 * Итог контрольного теста.
 *
 * Отказ означает только незачёт в рейтинг: прогресс пользователя сохраняется
 * в любом случае (ТЗ FR-VT-04).
 *
 * Экран называет то, что произошло на самом деле. До этого этапа он знал
 * ровно два состояния — вердикт и `null`, — и на любое `null` говорил «нет
 * связи с сервером». Гость, прошедший контрольный тест, получал именно эту
 * фразу, хотя связь была, а сервер отвечал «требуется вход»; ту же фразу
 * человек видел и в те секунды, пока ответ ещё шёл. Приложение не утверждает
 * того, чего не видело (ТЗ FR-EN-80).
 *
 * И он называет сам результат. Раньше экран отвечал на один вопрос — «пойдёт
 * ли это в рейтинг», — а на «что я набрал» и «какой у меня уровень» молчал:
 * метрики прогона до него просто не доезжали, хотя лежали в двух строках от
 * вызова. Контрольный тест — самое строгое измерение в продукте (незнакомый
 * текст, без подсказок, без исправлений), и молчать о его числах было хуже
 * всего именно здесь.
 */

import type { Translate } from '@type2x/i18n';
import type { TypingMetrics } from '@type2x/shared-contracts';
import { Button, Card, MetricsBar, ResultMark } from '@type2x/ui';
import type { ControlSubmission } from '@type2x/feature-control-test';
// Шкала уровня живёт в диагностике, потому что появилась там первой. Своей
// копии здесь нет намеренно: два разных представления о том, что такое
// «уверенный», означали бы, что слово ничего не значит.
import { levelForCpm } from '@type2x/feature-diagnostics';
import { meetsVerifiedRequirements } from '@type2x/learning-engine';

export interface ControlResultScreenProps {
  readonly t: Translate;
  /** `null` — ответ ещё не пришёл; экран показывается сразу, не дожидаясь его. */
  readonly outcome: ControlSubmission | null;
  /** Метрики прогона: они известны сразу и от вердикта не зависят. */
  readonly metrics: TypingMetrics;
  readonly onBack: () => void;
  /** Путь к аккаунту: контрольный результат живёт в нём. */
  readonly onSaveProgress: () => void;
}

export function ControlResultScreen({
  t,
  outcome,
  metrics,
  onBack,
  onSaveProgress,
}: ControlResultScreenProps): React.JSX.Element {
  /*
   * Уровень называется по тем же правилам, что и после диагностики: скорость
   * с поправкой на точность набора. Здесь поправка почти ничего не меняет —
   * исправлять во время теста нельзя, и `accuracy` совпадает с
   * `firstTryAccuracy`, — но правило одно на оба экрана, а не два похожих.
   */
  const effectiveCpm = metrics.cpm * metrics.firstTryAccuracy;

  /*
   * Ступень называется только по прогону, которого на неё хватает. Те же
   * требования, что и у зачёта в рейтинг: короткий или неточный прогон не
   * даёт основания ни для рейтинга, ни для слова о человеке. Отказ по этой
   * причине объясняет `control.belowRequirements` строкой ниже.
   */
  const enough = meetsVerifiedRequirements(
    metrics.accuracy,
    metrics.elapsedMs,
    metrics.typedCharacters,
  );

  return (
    <Card title={t('control.title')}>
      {outcome === null && <p role="status">{t('control.waiting')}</p>}

      {outcome?.kind === 'needs-account' && (
        <>
          <p role="alert">{t('control.needsAccount')}</p>
          <Button onClick={onSaveProgress}>{t('account.title')}</Button>
        </>
      )}

      {outcome?.kind === 'offline' && <p role="alert">{t('control.offline')}</p>}

      {outcome?.kind === 'not-accepted' && <p role="alert">{t('control.notAccepted')}</p>}

      {/*
        Успешное оформление — только при подтверждённом результате (ТЗ UI-52).
        Знак и текст стоят рядом с цветом: «пройден» и «не пройден» обязаны
        различаться и в оттенках серого.
      */}
      {outcome?.kind === 'verdict' && (
        <p
          className={`t2x-verdict t2x-verdict--${outcome.verdict.accepted ? 'accepted' : 'rejected'}`}
        >
          {/* Успешный знак — только при подтверждённом результате: у неуспеха
              он спокойный, а не победный (ТЗ UI-69). */}
          <ResultMark tone={outcome.verdict.accepted ? 'success' : 'neutral'} />
          <span className="t2x-verdict__mark" aria-hidden="true">
            {outcome.verdict.accepted ? '✓' : '✗'}
          </span>
          {outcome.verdict.accepted ? t('control.accepted') : t('control.rejected')}
        </p>
      )}

      {/*
        Уровень и числа стоят выше причин и рейтинга и показываются при любом
        исходе, включая ожидание ответа: набор состоялся, и его результат —
        факт о человеке, а не о сервере. Отказ в зачёте ничего в этих числах
        не меняет.
      */}
      {enough ? (
        <p className="t2x-level">
          <span className="t2x-level__label">{t('control.levelTitle')}</span>{' '}
          {t(`skillLevel.${levelForCpm(effectiveCpm)}`)}
          {' · '}
          <span className="t2x-level__cpm">
            {t('skillLevel.effectiveCpm', { cpm: Math.round(effectiveCpm) })}
          </span>
        </p>
      ) : (
        <p className="t2x-muted">{t('control.levelUnknown')}</p>
      )}

      <MetricsBar
        metrics={metrics}
        labels={{
          wpm: t('metrics.wpm'),
          cpm: t('metrics.cpm'),
          accuracy: t('metrics.accuracy'),
          rhythm: t('metrics.rhythm'),
          corrections: t('metrics.corrections'),
        }}
      />

      {outcome?.kind === 'verdict' && (
        <>
          {/*
            Почему не засчитан: причина приходит от сервера кодом, и здесь
            показываются те, которые мы умеем назвать словами. Незнакомый код
            молчит — выдумывать объяснение хуже, чем не дать его.
          */}
          {outcome.verdict.reasons
            .filter((reason) => reason === 'below_minimum_requirements')
            .map((reason) => (
              <p key={reason} className="t2x-muted">
                {t('control.belowRequirements')}
              </p>
            ))}

          {outcome.verdict.retentionCounted && (
            <p className="t2x-xp">{t('control.retentionCounted')}</p>
          )}

          {outcome.verdict.rating !== null && (
            <p className="t2x-xp">{t('control.rating', { rating: outcome.verdict.rating })}</p>
          )}
        </>
      )}

      <Button variant="secondary" onClick={onBack}>
        {t('control.back')}
      </Button>
    </Card>
  );
}
